/**
 * Etrenzik Design — style library and Style DNA engine.
 *
 * The customer picks 5 to 10 photographs out of about 50. That is the entire
 * user-facing interaction, and everything downstream — the concept renders, the
 * written brief, the PDF, the conversation the team has with the owner — is derived
 * from those picks. So the value of this product is almost entirely in how well
 * the photographs are tagged.
 *
 * Tag every image by hand. Do not let a model tag them unreviewed. Fifty images
 * per vertical times nine axes is a few hours of work that the product then runs
 * on for years.
 */

// ─── Tag vocabulary ─────────────────────────────────────────────────────────

export const MASSING = {
  'single-gable': 'One simple gabled volume',
  'cross-gable': 'Intersecting gables',
  'low-box': 'Low horizontal box',
  'stacked-box': 'Stacked or shifted boxes',
  'stepped': 'Stepped heights across the front',
  'l-shape': 'L-shaped, wrapping a courtyard or entry',
  'linear-bar': 'Long linear bar',
  'central-tower': 'A tall central element with lower wings',
  'campus': 'Several connected volumes',
  'barrel-arch': 'Curved or arched roof volume',
  'monitor': 'Raised center monitor with clerestory',
  'sawtooth': 'Sawtooth roof profile',
} as const;

export const ROOF = {
  'steep-gable': 'Steep gable',
  'shallow-gable': 'Shallow gable',
  'hip': 'Hip roof',
  'flat-parapet': 'Flat roof behind a parapet',
  'standing-seam': 'Standing-seam metal',
  'shed': 'Single-slope shed',
  'butterfly': 'Butterfly or inverted',
  'curved': 'Curved or vaulted',
  'mansard': 'Mansard',
  'tile': 'Clay or concrete tile',
} as const;

export const MATERIAL = {
  brick: 'Brick',
  'brick-painted': 'Painted brick',
  'split-face-cmu': 'Split-face block',
  stone: 'Natural or cast stone',
  stucco: 'Stucco or EIFS',
  'metal-panel': 'Metal wall panel',
  'metal-ribbed': 'Ribbed or corrugated metal',
  'board-batten': 'Board and batten',
  wood: 'Wood siding or timber',
  'tilt-concrete': 'Exposed concrete panel',
  'precast-textured': 'Textured precast',
  'curtain-wall': 'Glass curtain wall',
  'fiber-cement': 'Fiber cement panel',
  'brick-limestone': 'Brick with limestone trim',
} as const;

export const GLAZING = {
  minimal: 'Very little glass',
  punched: 'Individual punched windows',
  ribbon: 'Continuous ribbon windows',
  storefront: 'Ground-level storefront band',
  'full-height': 'Floor-to-ceiling glass',
  clerestory: 'High clerestory glass only',
  'gable-glass': 'A large glass gable or entry wall',
} as const;

export const ENTRY = {
  flush: 'Flush in the wall',
  recessed: 'Recessed into the volume',
  canopy: 'Under a projecting canopy',
  portico: 'Columned portico',
  tower: 'Through a tower element',
  'glass-atrium': 'A glass atrium or lobby',
  'porte-cochere': 'Drive-under porte-cochere',
  'corner-cut': 'A cut or angled corner',
} as const;

export const PALETTE = {
  'warm-earth': 'Warm earth tones',
  'cool-grey': 'Cool greys and silver',
  'monochrome-dark': 'Dark, near-monochrome',
  'bright-white': 'White and bright',
  'brick-red': 'Red brick dominant',
  'two-tone': 'Two strong contrasting colors',
  'natural-wood': 'Natural wood and green',
  'tan-beige': 'Tan and beige',
} as const;

export const ERA = {
  contemporary: 'Contemporary',
  'traditional-brick': 'Traditional brick',
  colonial: 'Colonial or Georgian',
  'gothic-revival': 'Gothic revival',
  mission: 'Mission or Spanish',
  'mid-century': 'Mid-century modern',
  farmhouse: 'Modern farmhouse',
  industrial: 'Industrial or warehouse',
  classical: 'Classical or civic',
  rustic: 'Rustic timber and stone',
  minimalist: 'Minimalist',
  'high-tech': 'High-tech or expressed structure',
} as const;

export const SIGNATURE = {
  none: 'No single signature element',
  steeple: 'Steeple or spire',
  'clock-tower': 'Clock or bell tower',
  cupola: 'Cupola',
  colonnade: 'Colonnade',
  'dock-doors': 'Expressed dock doors',
  'exposed-structure': 'Exposed structural frame',
  'deep-overhang': 'Deep roof overhang',
  'screening-louvers': 'Screening louvers or fins',
  'large-signage': 'Signage as a design element',
  'water-feature': 'Water feature or plaza',
  'covered-walk': 'Covered walkway',
} as const;

export type Massing = keyof typeof MASSING;
export type Roof = keyof typeof ROOF;
export type Material = keyof typeof MATERIAL;
export type Glazing = keyof typeof GLAZING;
export type Entry = keyof typeof ENTRY;
export type Palette = keyof typeof PALETTE;
export type Era = keyof typeof ERA;
export type Signature = keyof typeof SIGNATURE;

/** 0 = none, 4 = strongly present. */
export type Scale04 = 0 | 1 | 2 | 3 | 4;

export interface StyleTags {
  massing: Massing;
  roof: Roof;
  primaryMaterial: Material;
  accentMaterial?: Material;
  glazing: Glazing;
  entry: Entry;
  palette: Palette;
  era: Era;
  signature: Signature;
  /** How much decoration, trim, and detail. */
  ornament: Scale04;
  /** Horizontal and low at 0, tall and vertical at 4. */
  verticality: Scale04;
  /** Asymmetric at 0, mirror-symmetrical at 4. */
  symmetry: Scale04;
}

export interface StyleImage {
  id: string;
  /** Which verticals this image appears in. One image can serve several. */
  verticalIds: string[];
  r2Key: string;
  /** Required. Where the photo came from and under what license. */
  credit: string;
  /** Optional short caption; keep it descriptive, never evaluative. */
  caption?: string;
  tags: StyleTags;
  active: boolean;
}

// ─── Style DNA ──────────────────────────────────────────────────────────────

const CATEGORICAL_AXES = [
  'massing',
  'roof',
  'primaryMaterial',
  'glazing',
  'entry',
  'palette',
  'era',
  'signature',
] as const;

const NUMERIC_AXES = ['ornament', 'verticality', 'symmetry'] as const;

/** Axes where a split verdict genuinely changes the building. */
const HIGH_SALIENCE = new Set(['era', 'primaryMaterial', 'glazing', 'massing']);

export interface WeightedValue {
  value: string;
  label: string;
  weight: number;
}

export interface Conflict {
  axis: string;
  axisLabel: string;
  /** The competing options, for a "which of these two?" prompt to the customer. */
  options: WeightedValue[];
  message: string;
}

export interface StyleDNA {
  categorical: Record<string, WeightedValue[]>;
  numeric: Record<string, { mean: number; spread: number }>;
  conflicts: Conflict[];
  imageIds: string[];
}

const AXIS_LABELS: Record<string, string> = {
  massing: 'Overall shape',
  roof: 'Roof',
  primaryMaterial: 'Main material',
  glazing: 'Windows and glass',
  entry: 'Entrance',
  palette: 'Color',
  era: 'Overall style',
  signature: 'Signature feature',
  ornament: 'Level of detail',
  verticality: 'Height and proportion',
  symmetry: 'Symmetry',
};

const VOCAB: Record<string, Record<string, string>> = {
  massing: MASSING,
  roof: ROOF,
  primaryMaterial: MATERIAL,
  glazing: GLAZING,
  entry: ENTRY,
  palette: PALETTE,
  era: ERA,
  signature: SIGNATURE,
};

/**
 * Turn 5–10 picked images into a style profile.
 *
 * Conflicts are surfaced, not smoothed over. When a building committee's picks
 * split evenly between red brick and glass curtain wall, that is real information
 * and the tool should say so before anyone spends money.
 */
export function computeStyleDNA(picked: StyleImage[]): StyleDNA {
  if (picked.length === 0) {
    return { categorical: {}, numeric: {}, conflicts: [], imageIds: [] };
  }

  const categorical: Record<string, WeightedValue[]> = {};
  const conflicts: Conflict[] = [];

  for (const axis of CATEGORICAL_AXES) {
    const counts = new Map<string, number>();
    for (const img of picked) {
      const v = img.tags[axis] as string | undefined;
      if (!v) continue;
      counts.set(v, (counts.get(v) ?? 0) + 1);
      // An accent material counts at half weight toward the material axis.
      if (axis === 'primaryMaterial' && img.tags.accentMaterial) {
        const a = img.tags.accentMaterial;
        counts.set(a, (counts.get(a) ?? 0) + 0.5);
      }
    }

    const total = [...counts.values()].reduce((s, n) => s + n, 0) || 1;
    const ranked: WeightedValue[] = [...counts.entries()]
      .map(([value, n]) => ({
        value,
        label: VOCAB[axis]?.[value] ?? value,
        weight: n / total,
      }))
      .sort((a, b) => b.weight - a.weight);

    categorical[axis] = ranked.slice(0, 2);

    if (
      HIGH_SALIENCE.has(axis) &&
      ranked.length > 1 &&
      ranked[0].weight - ranked[1].weight < 0.2
    ) {
      conflicts.push({
        axis,
        axisLabel: AXIS_LABELS[axis],
        options: ranked.slice(0, 2),
        message:
          `Your picks are split on ${AXIS_LABELS[axis].toLowerCase()} — about half lean ` +
          `toward ${ranked[0].label.toLowerCase()} and about half toward ` +
          `${ranked[1].label.toLowerCase()}. Pick one to lead, or we will blend them.`,
      });
    }
  }

  const numeric: Record<string, { mean: number; spread: number }> = {};
  for (const axis of NUMERIC_AXES) {
    const vals = picked.map((i) => i.tags[axis] as number);
    const mean = vals.reduce((s, n) => s + n, 0) / vals.length;
    const spread = Math.sqrt(
      vals.reduce((s, n) => s + (n - mean) ** 2, 0) / vals.length,
    );
    numeric[axis] = { mean: Number(mean.toFixed(2)), spread: Number(spread.toFixed(2)) };

    if (spread > 1.2) {
      conflicts.push({
        axis,
        axisLabel: AXIS_LABELS[axis],
        options: [],
        message:
          `Your picks range widely on ${AXIS_LABELS[axis].toLowerCase()}. ` +
          `Some are much more ${axis === 'ornament' ? 'detailed' : axis === 'verticality' ? 'vertical' : 'symmetrical'} ` +
          `than others. Worth settling before we go further.`,
      });
    }
  }

  return { categorical, numeric, conflicts, imageIds: picked.map((i) => i.id) };
}

// ─── Outputs ────────────────────────────────────────────────────────────────

export interface BriefInputs {
  verticalLabel: string;
  sizeSf: { low: number; high: number } | number;
  stories: string;
  constructionSystemLabel: string;
  dna: StyleDNA;
}

const scaleWord = (axis: string, v: number): string => {
  const bands: Record<string, string[]> = {
    ornament: ['completely plain', 'very restrained', 'moderately detailed', 'richly detailed', 'highly ornamented'],
    verticality: ['long and low', 'mostly horizontal', 'evenly balanced', 'tall-feeling', 'strongly vertical'],
    symmetry: ['deliberately asymmetric', 'loosely balanced', 'broadly symmetrical', 'largely symmetrical', 'strictly symmetrical'],
  };
  return bands[axis]?.[Math.round(v)] ?? '';
};

/**
 * The written design brief. This matters as much as the renders — a board can't
 * hand an architect a JPEG, but it can hand them this paragraph.
 */
export function buildDesignBrief(input: BriefInputs): string {
  const { dna } = input;
  const top = (axis: string) => dna.categorical[axis]?.[0]?.label.toLowerCase() ?? '';
  const second = (axis: string) => dna.categorical[axis]?.[1]?.label.toLowerCase();

  const size =
    typeof input.sizeSf === 'number'
      ? `${input.sizeSf.toLocaleString()} sf`
      : `${input.sizeSf.low.toLocaleString()} to ${input.sizeSf.high.toLocaleString()} sf`;

  const material = second('primaryMaterial')
    ? `${top('primaryMaterial')} with ${second('primaryMaterial')} accents`
    : top('primaryMaterial');

  const article = (word: string) => (/^[aeiou]/i.test(word) ? 'An' : 'A');
  const signature =
    top('signature') && top('signature') !== 'no single signature element'
      ? ` ${article(top('signature'))} ${top('signature')} is the element people will remember.`
      : '';

  return [
    `A ${size} ${input.verticalLabel.toLowerCase()} building, ${input.stories === '1' ? 'single story' : `${input.stories} stories`}, built as ${input.constructionSystemLabel.toLowerCase()}.`,
    `The look leans ${top('era')} — ${top('massing')} under ${top('roof')}, clad primarily in ${material}.`,
    `Glass is handled as ${top('glazing')}. The entrance reads as ${top('entry')}, in a ${top('palette')} palette.`,
    `Proportions are ${scaleWord('verticality', dna.numeric.verticality?.mean ?? 2)} and ${scaleWord('symmetry', dna.numeric.symmetry?.mean ?? 2)}, with detailing that is ${scaleWord('ornament', dna.numeric.ornament?.mean ?? 2)}.${signature}`,
  ].join(' ');
}

/**
 * Compose the text-to-image prompt. Order matters: subject and scale first,
 * then form, then materials, then context, then camera.
 */
export function buildRenderPrompt(input: BriefInputs & { extraNotes?: string }): string {
  const { dna } = input;
  const top = (axis: string) => dna.categorical[axis]?.[0]?.label.toLowerCase() ?? '';
  const size =
    typeof input.sizeSf === 'number'
      ? input.sizeSf
      : Math.round((input.sizeSf.low + input.sizeSf.high) / 2);

  return [
    `Exterior of a ${size.toLocaleString()} square foot ${input.verticalLabel.toLowerCase()} building`,
    `${input.stories === '1' ? 'single story' : `${input.stories} stories`}, ${input.constructionSystemLabel.toLowerCase()} construction`,
    `${top('massing')}, ${top('roof')}`,
    `clad in ${top('primaryMaterial')}${dna.categorical.primaryMaterial?.[1] ? ` with ${second_(dna, 'primaryMaterial')} accents` : ''}`,
    `${top('glazing')}, entrance ${top('entry')}`,
    `${top('palette')} color palette, ${scaleWord('ornament', dna.numeric.ornament?.mean ?? 2)} detailing`,
    top('signature') !== 'no single signature element' ? top('signature') : '',
    'suburban site in Georgia, mature pines and low landscaping, paved parking in the foreground',
    'three-quarter exterior view, eye level, bright overcast daylight, no people',
    'architectural visualization, photorealistic, sharp detail',
    input.extraNotes ?? '',
  ]
    .filter(Boolean)
    .join(', ');
}

const second_ = (dna: StyleDNA, axis: string) =>
  dna.categorical[axis]?.[1]?.label.toLowerCase() ?? '';

export const NEGATIVE_PROMPT =
  'people, faces, text, watermark, signage text, logos, blurry, distorted geometry, ' +
  'floating structures, interior view, aerial view, night, cartoon, illustration, sketch';

/**
 * Required on every render, every export, and every page footer.
 * Do not make this configurable and do not let it be dismissed.
 */
export const RENDER_DISCLAIMER =
  'Concept illustration only. Not construction documents. Etrenzik Design does not ' +
  'provide architectural or engineering services. Final design must be prepared and ' +
  'sealed by a licensed design professional.';

/** How many images the customer sees, and how many they must pick. */
export const PICKER = { shown: 50, min: 5, max: 10 } as const;
